import * as React from 'react';
import { alpha, styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import ChangeCircleOutlined from '@mui/icons-material/ChangeCircleOutlined';
import PersonOffOutlined from '@mui/icons-material/PersonOffOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';

export type ParticipantAction = 'change-identifier' | 'change-status' | 'withdraw' | 'change-site' | null;

interface ParticipantActionMenuProps {
  onAction: (action: ParticipantAction) => void;
}

interface ParticipantMenuAction {
  label: string;
  value: ParticipantAction;
  icon: React.ReactNode;
}

const menuActions: ParticipantMenuAction[] = [
  {
    label: 'Change Identifier',
    value: 'change-identifier',
    icon: <BadgeOutlined />,
  },
  {
    label: 'Change Status',
    value: 'change-status',
    icon: <ChangeCircleOutlined />,
  },
  {
    label: 'Withdraw',
    value: 'withdraw',
    icon: <PersonOffOutlined />,
  },
  {
    label: 'Change Site',
    value: 'change-site',
    icon: <LocalHospitalOutlined />,
  },
];

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 200,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',

    '& .MuiMenu-list': {
      padding: '4px 0',
    },

    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 20,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },

      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

function ParticipantActionMenu({ onAction }: ParticipantActionMenuProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: ParticipantAction) => {
    setAnchorEl(null);
    onAction(action);
  };

  return (
    <>
      <IconButton
        id="participant-menu-button"
        aria-label="Open participant actions menu"
        aria-controls={open ? 'participant-menu' : undefined}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        size="small"
      >
        <MoreHorizIcon aria-hidden="true" />
      </IconButton>

      <StyledMenu
        id="participant-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'participant-menu-button',
        }}
      >
        {menuActions.slice(0, 2).map((action) => (
          <MenuItem key={action.value} onClick={() => handleAction(action.value)} disableRipple>
            {action.icon}
            {action.label}
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        {menuActions.slice(2).map((action) => (
          <MenuItem key={action.value} onClick={() => handleAction(action.value)} disableRipple>
            {action.icon}
            {action.label}
          </MenuItem>
        ))}
      </StyledMenu>
    </>
  );
}

export default ParticipantActionMenu;
